import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { EditorEvents, Extensions } from '@tiptap/core';
import Placeholder from '@tiptap/extension-placeholder';
import TextAlign from '@tiptap/extension-text-align';
import { Content, Editor, EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useTranslation } from 'next-i18next';
import { forwardRef, useImperativeHandle } from 'react';
import { EditorToolbar } from '../EditorToolbar/EditorToolbar';
import { ImageResize } from '../ImageExtension/ImageResize';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TableRow from '@tiptap/extension-table-row';
import { Table } from '../table';
import Link from '@tiptap/extension-link';

export interface TipTapEditorProps {
  debug?: boolean;
  showHTML?: boolean;
  extensions?: Extensions;
  placeholder?: string;
  content?: Content;
  onAddImage?: (editor: Editor) => void;
  onUpdate?: (props: EditorEvents['update']) => void;
  onFocus?: (props: EditorEvents['focus']) => void;
  onBlur?: (props: EditorEvents['blur']) => void;
}

/**
 * A rich text editor component built with TipTap.
 * @param debug - Whether to enable debug mode.
 * @param showHTML - Whether to show the HTML code of the editor content.
 * @param extensions - Additional TipTap extensions to be used.
 * @param placeholder - Placeholder text to be displayed when the editor is empty.
 * @param content - The initial content of the editor.
 * @param onAddImage - Callback function to be called when an image is added to the editor.
 * @param onUpdate - Callback function to be called when the editor content is updated.
 * @param onFocus - Callback function to be called when the editor gains focus.
 * @param onBlur - Callback function to be called when the editor loses focus.
 */
export const TipTapEditor = forwardRef<Editor | null, TipTapEditorProps>(
  (
    {
      debug,
      showHTML,
      placeholder,
      content,
      onAddImage,
      onUpdate,
      extensions = [],
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const editor = useEditor({
      extensions: [
        StarterKit,
        TextAlign.configure({
          types: ['heading', 'paragraph'],
        }),
        ImageResize,
        Placeholder.configure({
          // Use a placeholder:
          placeholder: placeholder ? placeholder : t('Write something …') || '',
          // Use different placeholders depending on the node type:
          // placeholder: ({ node }) => {
          //   if (node.type.name === 'heading') {
          //     return 'What’s the title?'
          //   }

          //   return 'Can you add some further context?'
          // },
        }),
        Table.configure({
          resizable: true,
          HTMLAttributes: {
            class: 'summary-table',
          },
        }),
        TableRow,
        TableHeader,
        TableCell,
        Link,
        ...extensions,
      ],
      content,
      onUpdate,
    });

    useImperativeHandle(
      ref,
      () => {
        return editor as Editor;
      },
      [editor],
    );

    return (
      <Box
        sx={[
          {
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            '.image-wrapper img': { display: 'block' },
            '.image-float-left': {
              marginRight: 2,
            },
            '.image-float-right': {
              marginLeft: 2,
            },
            '.ProseMirror p.is-editor-empty:first-of-type::before': {
              content: 'attr(data-placeholder)',
              color: 'text.disabled',
              float: 'left',
              height: 0,
              pointerEvents: 'none',
            },
            '.ProseMirror-gapcursor:after': {
              borderTop: (theme) =>
                `1px solid ${theme.palette['text']?.primary}`,
            },
            '.ProseMirror .telepointer': {
              position: 'relative',
              transition: 'opacity 200ms',
              '&.telepointer-selection': {
                lineHeight: '1.2',
                pointerEvents: 'none',
                userSelect: 'none',
              },
              '&.telepointer-selection-badge::after': {
                content: 'attr(data-initial)',
                position: 'absolute',
                display: 'block',
                top: '-14px',
                fontSize: '9px',
                padding: '2px 4px',
                left: '-1px',
                borderRadius: '2px 2px 2px 0',
                lineHeight: 'initial',
                width: 'max-content',
                backgroundColor: 'var(--badge-bg-color)',
              },
              '&.telepointer-dim': {
                opacity: '0.2',
              },
            },
            '.ProseMirror': {
              a: {
                color: (theme) => theme.palette.primary.main,
              },
              '.tableWrapper': {
                padding: '1rem 0',
                overflowX: 'auto',
              },

              '.resize-cursor': {
                // cursor: 'ew-resize',
                cursor: 'col-resize',
              },

              table: {
                borderCollapse: 'collapse',
                margin: 0,
                overflow: 'hidden',
                tableLayout: 'fixed',
                width: '100%',
              },

              'table td, table th': {
                border: '2px solid #ced4da',
                boxSizing: 'border-box',
                minWidth: '1em',
                padding: '3px 5px',
                position: 'relative',
                verticalAlign: 'top',
              },

              'table td > *, table th > *': {
                marginBottom: 0,
              },

              'table th': {
                backgroundColor: (theme) => theme.palette.background.paper,
                fontWeight: 'bold',
                textAlign: 'left',
              },

              'table .selectedCell:after': {
                background: 'rgba(200, 200, 255, 0.4)',
                content: '""',
                left: 0,
                right: 0,
                top: 0,
                bottom: 0,
                pointerEvents: 'none',
                position: 'absolute',
                zIndex: 2,
              },

              'table .column-resize-handle': {
                backgroundColor: '#adf',
                bottom: '-2px',
                position: 'absolute',
                right: '-2px',
                pointerEvents: 'none',
                top: 0,
                width: '4px',
              },

              'table p': {
                margin: 0,
              },
            },
          },
        ]}
      >
        <EditorToolbar
          editor={editor}
          onAddImage={
            onAddImage && editor ? () => onAddImage(editor) : undefined
          }
        />
        <Paper
          role="textbox"
          aria-multiline="true"
          component={EditorContent}
          editor={editor}
          sx={{
            '.ProseMirror': { border: 0, p: 2 },
          }}
        />
        {showHTML && editor ? (
          <Paper sx={{ p: 2 }}>
            <Typography variant="caption" component="div">
              {t('HTML Markup')}
            </Typography>
            {editor.getHTML()}
          </Paper>
        ) : null}
        {showHTML && editor ? (
          <Paper sx={{ p: 2 }}>
            <Typography variant="caption" component="div">
              {t('Rendered HTML Content')}
            </Typography>
            <div dangerouslySetInnerHTML={{ __html: editor.getHTML() }} />
          </Paper>
        ) : null}
      </Box>
    );
  },
);
